import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ShoppingBag,
  User,
  Menu,
  X,
  LogOut,
  Heart,
  ChevronDown,
  ChevronRight,
  Phone,
  Tag,
  Sparkles,
  Gift,
  Star,
} from "lucide-react";

import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import { useAuth } from "../../context/AuthContext";
import { useSettings } from "../../context/SettingsContext";
import { imgUrl } from "../../lib/api";
import api from "../../lib/api";
import SearchBox from "./SearchBox";

export default function Header() {
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, logout } = useAuth();
  const { settings } = useSettings();

  const loc = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] =
    useState(false);

  /* ============================================================
     LOAD CATEGORIES
  ============================================================ */

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      try {
        const res = await api.get("/categories");

        if (mounted) {
          setCategories(
            Array.isArray(res.data)
              ? res.data
              : []
          );
        }
      } catch (error) {
        console.error(
          "Failed to load categories:",
          error
        );
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, []);

  /* ============================================================
     CLOSE MENUS ON ROUTE CHANGE
  ============================================================ */

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
    setMobileCategoriesOpen(false);
  }, [loc.pathname]);

  /* ============================================================
     SETTINGS
  ============================================================ */

  const shopName =
    settings?.shop_name ||
    "Φαρμακείο Κατιρτζόγλου";

  const phone =
    settings?.contact?.phone ||
    "23210 62270";

  /* ============================================================
     CATEGORY HELPERS
  ============================================================ */

  const mainCategories = categories.filter(
    (category) =>
      !category.parent_id ||
      category.parent_id === ""
  );

  const getCategoryId = (category) =>
    category.id || category._id;

  const getChildren = (parentId) =>
    categories.filter(
      (category) =>
        String(category.parent_id || "") ===
        String(parentId)
    );

  const buildCategoryPath = (category) => {
    const parts = [];
    const visited = new Set();

    let current = category;

    while (current) {
      const currentId = String(
        current.id ||
          current._id ||
          ""
      );

      if (visited.has(currentId)) {
        break;
      }

      visited.add(currentId);

      if (!current.slug) {
        break;
      }

      parts.unshift(current.slug);

      if (!current.parent_id) {
        break;
      }

      current = categories.find(
        (item) =>
          String(
            item.id ||
              item._id ||
              ""
          ) ===
          String(current.parent_id)
      );
    }

    if (parts.length === 0) {
      return "/shop";
    }

    return `/category/${parts.join("/")}`;
  };

  /* ============================================================
     CATEGORY ICONS
  ============================================================ */

  const categoryIcons = {
    "Ήλιος & Φροντίδα": "☀️",
    "Περιποίηση Προσώπου": "✨",
    "Gift Moments": "🎁",
    "Γυναικεία Φροντίδα": "👩",
    "Kids Care": "👶",
    "Συμπληρώματα Διατροφής & Βιταμίνες": "💊",
    "Αθλητική Διατροφή & Υγιεινά Σνακ": "💚",
    "Αξεσουάρ": "🎀",
    "Περιποίηση Μαλλιών": "💇",
    "Άμυνα στη Φύση": "🌿",
    "Men Essentials": "👨",
    "Περιποίηση Σώματος": "🧴",
    "Προσωπική Υγιεινή": "🧼",
    "Πρώτες Βοήθειες & Ανακούφιση": "🩹",
  };

  const getCategoryIcon = (name) =>
    categoryIcons[name] || "•";

  /* ============================================================
     SPECIAL NAVIGATION LINKS
  ============================================================ */

  const specialLinks = [
    {
      label: "Προσφορές",
      href: "/shop?offers=true",
      icon: Tag,
      color: "text-[#C25953]",
      hover: "hover:bg-[#FFF4F2]",
    },
    {
      label: "Νέα προϊόντα",
      href: "/shop?sort=newest",
      icon: Sparkles,
      color: "text-[#1E3A2F]",
      hover: "hover:bg-[#E8F0E1]",
    },
    {
      label: "Gift Moments",
      href: "/category/gift-moments",
      icon: Gift,
      color: "text-[#7A5C45]",
      hover: "hover:bg-[#F7F1EB]",
    },
    {
      label: "Best Sellers",
      href: "/shop?featured=true",
      icon: Star,
      color: "text-[#8A6A1F]",
      hover: "hover:bg-[#FAF6E8]",
    },
  ];

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <header
      className="sticky top-0 z-50 bg-white border-b border-[#E5E9E4]"
      onMouseLeave={() => setMegaOpen(false)}
    >

      {/* ======================================================
          ANNOUNCEMENT BAR
      ====================================================== */}

      {settings?.announcement ? (
        <div
          className="bg-[#1E3A2F] text-white text-center text-xs sm:text-sm py-2 px-4 tracking-wide"
          data-testid="announcement-bar"
        >
          {settings.announcement}
        </div>
      ) : null}

      {/* ======================================================
          MAIN HEADER
      ====================================================== */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="flex items-center justify-between h-[76px] gap-4">

          {/* ==================================================
              LOGO
          ================================================== */}

          <Link
            to="/"
            className="flex items-center gap-3 shrink-0"
            data-testid="header-logo"
            aria-label={`${shopName} - Αρχική`}
          >
            <img
              src={imgUrl(
                settings?.logo ||
                  "/logo.jpeg"
              )}
              alt={`${shopName} - Αρχική`}
              className="h-12 w-12 rounded-md object-cover"
            />

            <span className="font-serif-display text-xl sm:text-2xl leading-tight text-[#1E3A2F] hidden sm:block max-w-[190px]">
              {shopName}
            </span>
          </Link>

          {/* ==================================================
              DESKTOP SEARCH
          ================================================== */}

          <div className="hidden md:flex flex-1 max-w-[470px] mx-4">
            <SearchBox />
          </div>

          {/* ==================================================
              DESKTOP CONTACT
          ================================================== */}

          <a
            href={`tel:${phone.replace(
              /\s/g,
              ""
            )}`}
            className="hidden xl:flex items-center gap-2 text-[#1E3A2F] mr-1 shrink-0"
            aria-label={`Τηλέφωνο ${phone}`}
          >
            <Phone className="h-4 w-4" />

            <div className="leading-tight">
              <div className="text-[11px] text-[#789087]">
                Επικοινωνία
              </div>

              <div className="text-sm font-medium">
                {phone}
              </div>
            </div>
          </a>

          {/* ==================================================
              DESKTOP ACTIONS
          ================================================== */}

          <nav
            className="flex items-center gap-1 sm:gap-2"
            aria-label="Κύρια πλοήγηση"
          >

            {/* LOGIN */}

            {user && user.role !== "admin" ? (
              <button
                onClick={logout}
                className="hidden lg:flex items-center gap-1 px-2.5 py-2 text-sm text-[#536D61] hover:text-[#1E3A2F] transition-colors"
                data-testid="logout-button"
                type="button"
              >
                <LogOut className="h-4 w-4" />
                Έξοδος
              </button>
            ) : (
              <Link
                to="/login"
                className="hidden lg:flex items-center gap-1 px-2.5 py-2 text-sm text-[#536D61] hover:text-[#1E3A2F] transition-colors"
                data-testid="nav-login"
              >
                <User className="h-4 w-4" />
                Σύνδεση
              </Link>
            )}

            {/* WISHLIST */}

            <Link
              to="/wishlist"
              className="relative p-2.5 rounded-md hover:bg-[#E8F0E1] transition-colors"
              data-testid="wishlist-button"
              aria-label={`Αγαπημένα${
                wishCount > 0
                  ? `, ${wishCount} προϊόντα`
                  : ""
              }`}
            >
              <Heart
                className="h-5 w-5 text-[#1E3A2F]"
                aria-hidden="true"
              />

              {wishCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 bg-[#C25953] text-white text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center"
                  aria-hidden="true"
                >
                  {wishCount}
                </span>
              )}
            </Link>

            {/* CART */}

            <Link
              to="/cart"
              className="relative p-2.5 rounded-md hover:bg-[#E8F0E1] transition-colors"
              data-testid="cart-button"
              aria-label={`Καλάθι${
                count > 0
                  ? `, ${count} προϊόντα`
                  : ""
              }`}
            >
              <ShoppingBag
                className="h-5 w-5 text-[#1E3A2F]"
                aria-hidden="true"
              />

              {count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 bg-[#1E3A2F] text-white text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center"
                  data-testid="cart-count"
                  aria-hidden="true"
                >
                  {count}
                </span>
              )}
            </Link>

            {/* MOBILE MENU */}

            <button
              className="lg:hidden p-2"
              onClick={() =>
                setMobileOpen(
                  (value) => !value
                )
              }
              data-testid="mobile-menu-toggle"
              type="button"
              aria-label={
                mobileOpen
                  ? "Κλείσιμο μενού"
                  : "Άνοιγμα μενού"
              }
              aria-expanded={mobileOpen}
              aria-controls="mobile-navigation"
            >
              {mobileOpen ? (
                <X
                  className="h-6 w-6 text-[#1E3A2F]"
                  aria-hidden="true"
                />
              ) : (
                <Menu
                  className="h-6 w-6 text-[#1E3A2F]"
                  aria-hidden="true"
                />
              )}
            </button>
          </nav>
        </div>

        {/* ======================================================
            DESKTOP E-SHOP NAVIGATION
        ====================================================== */}

        <div className="hidden lg:flex items-center border-t border-[#EEF1ED] h-[50px]">

          {/* ==================================================
              CATEGORIES + MEGA MENU
          ================================================== */}

          <div
            className="relative h-full flex items-center"
            onMouseEnter={() =>
              setMegaOpen(true)
            }
          >

            <button
              type="button"
              className={`flex items-center gap-2 h-full px-5 text-sm font-semibold text-[#1E3A2F] transition-colors ${
                megaOpen
                  ? "bg-[#E8F0E1]"
                  : "hover:bg-[#E8F0E1]"
              }`}
              aria-expanded={megaOpen}
              aria-haspopup="true"
            >
              Κατηγορίες

              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${
                  megaOpen
                    ? "rotate-180"
                    : ""
                }`}
              />
            </button>

            {/* ==================================================
                MEGA MENU
            ================================================== */}

            {megaOpen && (
              <div
                className="absolute left-0 top-full w-[1000px] max-w-[calc(100vw-32px)] bg-white border border-[#E5E9E4] shadow-[0_18px_45px_rgba(30,58,47,0.14)] rounded-b-xl overflow-hidden"
                onMouseEnter={() =>
                  setMegaOpen(true)
                }
              >

                <div className="grid grid-cols-4">

                  {mainCategories.map(
                    (category) => {
                      const categoryId =
                        getCategoryId(
                          category
                        );

                      const children =
                        getChildren(
                          categoryId
                        );

                      return (
                        <div
                          key={
                            categoryId
                          }
                          className="p-5 border-r border-b border-[#EEF1ED] last:border-r-0 min-h-[145px]"
                        >

                          {/* MAIN CATEGORY */}

                          <Link
                            to={buildCategoryPath(
                              category
                            )}
                            className="group/category flex items-start gap-2.5 text-[#1E3A2F] font-semibold text-sm hover:text-[#536D61] transition-colors mb-3"
                          >
                            <span className="text-lg leading-none">
                              {getCategoryIcon(
                                category.name
                              )}
                            </span>

                            <span className="leading-snug">
                              {category.name}
                            </span>
                          </Link>

                          {/* SUBCATEGORIES */}

                          {children.length >
                            0 && (
                            <div className="space-y-1.5">

                              {children.map(
                                (
                                  child
                                ) => (
                                  <Link
                                    key={
                                      getCategoryId(
                                        child
                                      )
                                    }
                                    to={buildCategoryPath(
                                      child
                                    )}
                                    className="group/sub flex items-center gap-1.5 text-xs text-[#687B73] hover:text-[#1E3A2F] transition-colors"
                                  >
                                    <ChevronRight className="h-3 w-3 shrink-0 opacity-60 group-hover/sub:opacity-100" />

                                    <span>
                                      {
                                        child.name
                                      }
                                    </span>
                                  </Link>
                                )
                              )}

                              <Link
                                to={buildCategoryPath(
                                  category
                                )}
                                className="inline-flex items-center gap-1 pt-1 text-xs font-semibold text-[#1E3A2F] hover:text-[#536D61]"
                              >
                                Όλα στην κατηγορία
                                <ChevronRight className="h-3 w-3" />
                              </Link>
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>

                {/* MEGA MENU FOOTER */}

                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 bg-[#F7F9F5] border-t border-[#E5E9E4]">

                  <span className="text-xs text-[#687B73]">
                    Ανακαλύψτε προϊόντα υγείας,
                    ομορφιάς και καθημερινής
                    φροντίδας.
                  </span>

                  <Link
                    to="/shop"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E3A2F] hover:underline"
                  >
                    Δείτε όλα τα προϊόντα
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* ==================================================
              ALL PRODUCTS
          ================================================== */}

          <Link
            to="/shop"
            className="h-full flex items-center px-5 text-sm text-[#536D61] hover:text-[#1E3A2F] hover:bg-[#F7F9F5] transition-colors"
          >
            Όλα τα προϊόντα
          </Link>

          {/* ==================================================
              SPECIAL LINKS
          ================================================== */}

          {specialLinks.map(
            ({
              label,
              href,
              icon: Icon,
              color,
              hover,
            }) => (
              <Link
                key={label}
                to={href}
                className={`h-full flex items-center gap-1.5 px-4 text-sm font-medium ${color} ${hover} transition-colors`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </Link>
            )
          )}

          {/* ==================================================
              SHIPPING MESSAGE
          ================================================== */}

          <div className="ml-auto flex items-center pl-4 text-xs text-[#789087] whitespace-nowrap">
            Δωρεάν αποστολή άνω των 50€
          </div>
        </div>

        {/* ======================================================
            MOBILE NAVIGATION
        ====================================================== */}

        {mobileOpen && (
          <div
            id="mobile-navigation"
            className="lg:hidden pb-5 pt-2 space-y-1 border-t border-[#EEF1ED]"
            data-testid="mobile-menu"
          >

            {/* MOBILE SEARCH */}

            <div className="py-2">
              <SearchBox
                onNavigate={() =>
                  setMobileOpen(false)
                }
              />
            </div>

            {/* ALL PRODUCTS */}

            <Link
              to="/shop"
              className="flex items-center justify-between py-3 px-2 text-[#1E3A2F] font-semibold border-b border-[#EEF1ED]"
            >
              <span>Όλα τα προϊόντα</span>

              <ChevronRight className="h-4 w-4" />
            </Link>

            {/* MOBILE SPECIAL LINKS */}

            <div className="border-b border-[#EEF1ED] py-1">

              <Link
                to="/shop?offers=true"
                className="flex items-center gap-3 py-3 px-2 text-[#C25953] font-semibold"
              >
                <Tag className="h-4 w-4" />
                Προσφορές
              </Link>

              <Link
                to="/shop?sort=newest"
                className="flex items-center gap-3 py-3 px-2 text-[#1E3A2F] font-semibold"
              >
                <Sparkles className="h-4 w-4" />
                Νέα προϊόντα
              </Link>

              <Link
                to="/category/gift-moments"
                className="flex items-center gap-3 py-3 px-2 text-[#7A5C45] font-semibold"
              >
                <Gift className="h-4 w-4" />
                Gift Moments
              </Link>

              <Link
                to="/shop?featured=true"
                className="flex items-center gap-3 py-3 px-2 text-[#8A6A1F] font-semibold"
              >
                <Star className="h-4 w-4" />
                Best Sellers
              </Link>
            </div>

            {/* MOBILE CATEGORIES */}

            <button
              type="button"
              onClick={() =>
                setMobileCategoriesOpen(
                  (value) => !value
                )
              }
              className="w-full flex items-center justify-between py-3 px-2 text-[#1E3A2F] font-semibold border-b border-[#EEF1ED]"
            >
              <span>Κατηγορίες</span>

              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  mobileCategoriesOpen
                    ? "rotate-180"
                    : ""
                }`}
              />
            </button>

            {mobileCategoriesOpen && (
              <div className="pb-2">

                {mainCategories.map(
                  (category) => {
                    const categoryId =
                      getCategoryId(
                        category
                      );

                    const children =
                      getChildren(
                        categoryId
                      );

                    return (
                      <div
                        key={
                          categoryId
                        }
                        className="border-b border-[#F0F2EF]"
                      >

                        {/* MAIN CATEGORY */}

                        <Link
                          to={buildCategoryPath(
                            category
                          )}
                          className="flex items-center gap-2 py-3 px-3 text-sm font-medium text-[#1E3A2F]"
                        >
                          <span>
                            {getCategoryIcon(
                              category.name
                            )}
                          </span>

                          <span className="flex-1">
                            {category.name}
                          </span>

                          <ChevronRight className="h-4 w-4" />
                        </Link>

                        {/* SUBCATEGORIES */}

                        {children.length >
                          0 && (
                          <div className="pb-2 pl-9 pr-3 space-y-1">
                            {children.map(
                              (
                                child
                              ) => (
                                <Link
                                  key={
                                    getCategoryId(
                                      child
                                    )
                                  }
                                  to={buildCategoryPath(
                                    child
                                  )}
                                  className="block py-1.5 text-xs text-[#687B73] hover:text-[#1E3A2F]"
                                >
                                  {
                                    child.name
                                  }
                                </Link>
                              )
                            )}

                            <Link
                              to={buildCategoryPath(
                                category
                              )}
                              className="block pt-1 pb-2 text-xs font-semibold text-[#1E3A2F]"
                            >
                              Όλα στην κατηγορία →
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            )}

            {/* MOBILE LOGIN */}

            {user &&
            user.role !== "admin" ? (
              <button
                onClick={logout}
                className="w-full text-left py-3 px-2 text-[#536D61]"
                type="button"
              >
                <span className="flex items-center gap-2">
                  <LogOut className="h-4 w-4" />
                  Έξοδος
                </span>
              </button>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 py-3 px-2 text-[#536D61]"
              >
                <User className="h-4 w-4" />
                Σύνδεση / Εγγραφή
              </Link>
            )}

            {/* MOBILE PHONE */}

            <a
              href={`tel:${phone.replace(
                /\s/g,
                ""
              )}`}
              className="flex items-center gap-2 py-3 px-2 text-[#536D61]"
            >
              <Phone className="h-4 w-4" />
              {phone}
            </a>
          </div>
        )}
      </div>
    </header>
  );
}